/**
 * Template Registry
 *
 * Centralized management of service template configurations.
 * Each template folder contains its own config with:
 * - fields: Field configurations for chips/columns
 * - expandedRow: Renderer and expandable checker
 * - calculateQuota: How to calculate quota from configs
 *
 * To add a new template:
 * 1. Create folder: templates/{template-name}/
 * 2. Create config: templates/{template-name}/index.ts
 * 3. Import and add to `templates` array below
 */
import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'
import type { TemplateConfig, TemplateField } from './types'

// Import template configs
import { defaultTemplate } from './default'
import { vcloudTemplate } from './vcloud'
import { cloudTemplates } from './cloud'
import { serverTemplates } from './server'
import { emailTemplates } from './email'
import { vnisTemplates } from './vnis'
import { cdnTemplates } from './cdn'
import { domainTemplates } from './domain'
import { sslTemplates } from './ssl'
import { deviceTemplates } from './device'

// Re-export types
export type {
    TemplateConfig,
    TemplateField,
    TemplateRegistry,
    ExpandedRowConfig,
    QuotaCalculator,
    RegisterFormType,
    RegisterFormConfig,
    RegisterServiceTemplateProps,
} from './types'

// Re-export shared utilities
export * from './shared'

// Re-export template specifics for direct access
export * from './default'
export * from './vcloud'
export * from './cloud'
export * from './server'
export * from './email'
export * from './vnis'
export * from './cdn'
export * from './domain'
export * from './ssl'
export * from './device'

// ============================================================================
// Registry
// ============================================================================

const templates: TemplateConfig[] = [
    // VCloud (special grouped expanded row)
    vcloudTemplate,

    // Cloud (non-VCloud)
    ...cloudTemplates,

    // Server
    ...serverTemplates,

    // Email
    ...emailTemplates,

    // VNIS
    ...vnisTemplates,

    // CDN / SwiftFederation
    ...cdnTemplates,

    // Domain
    ...domainTemplates,

    // SSL
    ...sslTemplates,

    // Device
    ...deviceTemplates,

    // Default fallback (always last due to lowest priority)
    defaultTemplate,
].sort((a, b) => (b.priority || 0) - (a.priority || 0))

// ============================================================================
// Template Lookup
// ============================================================================

/**
 * Find matching template config for a service template name
 */
export const getTemplateConfig = (templateName?: string): TemplateConfig => {
    if (!templateName) return defaultTemplate

    const name = templateName.toLowerCase()

    for (const config of templates) {
        for (const pattern of config.patterns) {
            if (typeof pattern === 'string') {
                if (name.includes(pattern)) return config
            } else if (pattern.test(name)) {
                return config
            }
        }
    }

    return defaultTemplate
}

/**
 * Check if a template name matches a specific template config
 */
export const isTemplate = (
    templateName: string | undefined,
    templateId: string
): boolean => {
    const config = getTemplateConfig(templateName)
    return config.id === templateId
}

/**
 * Get template config by ID
 */
export const getTemplateById = (id: string): TemplateConfig | undefined => {
    return templates.find((t) => t.id === id)
}

/**
 * Get all registered template IDs
 */
export const getRegisteredTemplates = (): string[] => {
    return templates.map((t) => t.id)
}

// ============================================================================
// Field Utilities
// ============================================================================

/**
 * Get fields for a template
 */
export const getTemplateFields = (templateName?: string): TemplateField[] => {
    return getTemplateConfig(templateName).fields
}

/**
 * Get all field names (flattened) for a template
 * Used for chips filtering
 */
export const getTemplateFieldNames = (templateName?: string): string[] => {
    const fields = getTemplateFields(templateName)
    return fields.flatMap((f) => f.names)
}

/**
 * Get all possible fields (union of all templates)
 */
export const getAllFields = (): TemplateField[] => {
    const fieldMap = new Map<string, TemplateField>()
    templates.forEach((t) => {
        t.fields.forEach((f) => {
            if (!fieldMap.has(f.key)) {
                fieldMap.set(f.key, f)
            }
        })
    })
    return Array.from(fieldMap.values())
}

// ============================================================================
// Quota Utilities
// ============================================================================

/**
 * Calculate quota for a config option using template-specific calculator
 */
export const getConfigQuota = (config: any, templateName?: string): number => {
    const template = getTemplateConfig(templateName)
    if (template.calculateQuota) {
        return template.calculateQuota(config, templateName)
    }
    return config?.commitment_value || 0
}

// ============================================================================
// Register Form Utilities (Technical workflow)
// ============================================================================

import type { RegisterFormType } from './types'

/**
 * Get register form type for a service
 * Returns the appropriate form type based on template and product group
 */
export const getRegisterFormType = (
    templateName?: string,
    productGroupName?: string
): RegisterFormType => {
    const config = getTemplateConfig(templateName)

    // Check product group overrides first
    if (config.registerForm?.productGroupOverrides && productGroupName) {
        const override =
            config.registerForm.productGroupOverrides[productGroupName]
        if (override) return override
    }

    // Return template's register form type or default
    return config.registerForm?.type || 'default'
}

// ============================================================================
// Smart Expanded Row Factory
// ============================================================================

interface SmartExpandedRow {
    render: (row: any, index: number) => ReactNode
    isExpandable: (row: any) => boolean
}

/**
 * Create smart expanded row handlers that auto-select template-specific behavior
 * Caches renderers for performance
 */
export const createSmartExpandedRow = (t: TFunction): SmartExpandedRow => {
    // Cache renderers by template ID to avoid recreation
    const rendererCache = new Map<
        string,
        (row: any, index: number) => ReactNode
    >()

    const getRenderer = (templateName?: string) => {
        const config = getTemplateConfig(templateName)
        // Fallback to default template's expandedRow if missing
        const expandedRow = config.expandedRow ?? defaultTemplate.expandedRow

        if (!rendererCache.has(config.id)) {
            rendererCache.set(
                config.id,
                expandedRow.createRenderer(t, getTemplateFields)
            )
        }

        return rendererCache.get(config.id)!
    }

    return {
        render: (row: any, index: number) => {
            const renderer = getRenderer(row?.service_template)
            return renderer(row, index)
        },
        isExpandable: (row: any) => {
            const config = getTemplateConfig(row?.service_template)
            // Fallback to default template's expandedRow if missing
            const expandedRow =
                config.expandedRow ?? defaultTemplate.expandedRow
            return expandedRow.isExpandable(row)
        },
    }
}
